import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, AlertCircle, Rocket, Loader2, MessageCircle, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import confetti from 'canvas-confetti';
import { useExitWarning } from '../../lib/hooks';
import { RegisterSplitLayout } from '../../components/register/RegisterSplitLayout';
import { fireRegistrationConversion } from '../../lib/analytics/marketingTags';
import { trackRegisterStepViewed } from '../../lib/analytics/onboardingEvents';
import { collectAttributionData, ensureEventId } from '../../lib/attribution/attributionCookie';

// Messaggio leggibile da un errore di supabase.functions.invoke() (come in
// PasswordSetupPage): il motivo vero e' nel corpo della risposta, error.message e'
// la frase tecnica di supabase-js ("Edge Function returned a non-2xx status code").
async function completeInvokeErrorMessage(invokeError: any): Promise<string> {
  const fallback = 'Non siamo riusciti a completare la registrazione. Riprova tra qualche istante.';
  if (invokeError?.context && typeof invokeError.context.json === 'function') {
    try {
      const body = await invokeError.context.json();
      if (typeof body?.error === 'string' && !['Errore interno del server', 'Method not allowed'].includes(body.error)) {
        return body.error;
      }
    } catch {
      // corpo non JSON: si usa il messaggio generico
    }
  }
  if (invokeError?.name === 'FunctionsFetchError' || /failed to send|fetch/i.test(String(invokeError?.message ?? ''))) {
    return 'Connessione non riuscita. Controlla la rete e riprova.';
  }
  return fallback;
}

export function CompletePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionToken = searchParams.get('token');

  const [isProcessing, setIsProcessing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState<string>('');
  const [sessionData, setSessionData] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isWaitingForApi, setIsWaitingForApi] = useState(false);
  // Nuovo flusso: l'utente arriva già autenticato (auto-login dello step
  // password). In quel caso non si torna più su /login: si prosegue da soli
  // verso l'onboarding. Il percorso storico (resume anonimo fermo allo step
  // company) resta invariato: bottone "Accedi a Flusia".
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Warning on page exit during processing (not after completion)
  useExitWarning(isProcessing && !completed, 'La registrazione è in corso. Sei sicuro di voler uscire?');

  const processingMessages = [
    "Avvio della configurazione...",
    "Creazione dell'organizzazione...",
    "Configurazione dei moduli...",
    "Impostazione dei ruoli utente...",
    "Finalizzazione dell'account...",
    "Completamento registrazione..."
  ];

  const waitingMessages = [
    "🚀 Preparando il razzo per il decollo...",
    "✨ Aggiungendo un pizzico di magia...",
    "🎯 Ultimi ritocchi al tuo CRM perfetto...",
    "☕ Il caffè sta per essere servito...",
    "🔧 I nostri gnomi stanno lavorando sodo...",
    "🌟 Lucidando ogni angolo per te...",
    "🎁 Stiamo impacchettando tutto con cura...",
    "🏎️ Mettendo il turbo alla configurazione..."
  ];

  // Confetti with softer pastel colors
  const celebrateWithConfetti = () => {
    const colors = ['#93C5FD', '#86EFAC', '#FCD34D', '#FCA5A5', '#C4B5FD'];

    confetti({
      particleCount: 80,
      spread: 60,
      origin: { x: 0.15, y: 0.85 },
      colors,
      gravity: 0.8,
      scalar: 0.9
    });

    confetti({
      particleCount: 80,
      spread: 60,
      origin: { x: 0.85, y: 0.85 },
      colors,
      gravity: 0.8,
      scalar: 0.9
    });

    setTimeout(() => {
      confetti({
        particleCount: 40,
        spread: 40,
        origin: { x: 0.5, y: 0.75 },
        colors,
        gravity: 0.8,
        scalar: 0.9
      });
    }, 400);
  };

  useEffect(() => {
    if (!sessionToken) {
      navigate('/register');
      return;
    }

    trackRegisterStepViewed('complete');
    supabase.auth.getSession().then(({ data: { session } }) => setIsAuthenticated(!!session));
    handleCompleteRegistration();
  }, [sessionToken, navigate]);

  const handleCompleteRegistration = async () => {
    setIsProcessing(true);
    setError('');
    setProgress(0);
    setCurrentMessage(processingMessages[0]);

    try {
      const progressSteps = [
        { progress: 20, messageIndex: 1, delay: 700 },
        { progress: 40, messageIndex: 2, delay: 800 },
        { progress: 60, messageIndex: 3, delay: 700 },
        { progress: 80, messageIndex: 4, delay: 500 },
        { progress: 95, messageIndex: 5, delay: 400 },
      ];

      for (const step of progressSteps) {
        await new Promise(resolve => setTimeout(resolve, step.delay));
        setProgress(step.progress);
        setCurrentMessage(processingMessages[step.messageIndex]);
      }

      // Start waiting animation with rotating messages
      setIsWaitingForApi(true);
      let messageIndex = 0;
      const messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % waitingMessages.length;
        setCurrentMessage(waitingMessages[messageIndex]);
      }, 2000);

      try {
        const { data, error } = await supabase.functions.invoke('register-complete', {
          body: {
            sessionToken,
            skipPayment: true,
            // Consenso "ultimo noto" più fresco possibile (closest to conversion):
            // register-complete lo fonde con quello salvato in sessione da register-start.
            attributionData: ensureEventId(collectAttributionData())
          }
        });

        clearInterval(messageInterval);
        setIsWaitingForApi(false);

        if (error) {
          throw new Error(await completeInvokeErrorMessage(error));
        }
        if (!data?.success) throw new Error(data?.message || 'Registrazione non completata');

        setProgress(100);
        setSessionData(data);
        setCompleted(true);

        // Il token in mano al browser e' quello emesso dall'auto-login dello step
        // password: se l'organizzazione e' stata agganciata DOPO (registrazione
        // ripresa a meta', recupero di un profilo orfano) non porta il claim
        // app_metadata.organization_id, e flusia-core risponde 403 a ogni chiamata
        // (shared/auth/middleware.ts). Si vedeva allo step "Invita il tuo team":
        // POST /api/invites -> 403, in interfaccia solo "Errore, riprova".
        // Il claim lo scrive il trigger sync_organization_to_jwt su auth.users:
        // basta rinnovare la sessione qui per ripartire con un token completo.
        try {
          await supabase.auth.refreshSession();
        } catch (e) {
          console.warn('[register] refresh della sessione non riuscito', e);
        }

        try {
          // Email per Enhanced Conversions: sessione auth (auto-login dello
          // step password). gtag invia solo l'hash e rispetta ad_user_data.
          const { data: { session: authSession } } = await supabase.auth.getSession();
          if (data.conversion_eligible === true && typeof data.marketing_event_id === 'string') {
            fireRegistrationConversion(data.marketing_event_id, authSession?.user?.email ?? null);
          }
        } catch (e) {
          console.warn('[ads] client conversion fire failed', e);
        }

        setTimeout(() => {
          celebrateWithConfetti();
        }, 200);

        // Nuovo flusso: utente autenticato dallo step password → si prosegue
        // con l'onboarding senza rimbalzo su /login.
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session) {
            setTimeout(() => navigate('/register/discovery'), 1400);
          }
        });
      } catch (apiError) {
        clearInterval(messageInterval);
        setIsWaitingForApi(false);
        throw apiError;
      }

    } catch (error) {
      console.error('Registration completion error:', error);
      setError(error instanceof Error ? error.message : 'Errore durante la registrazione');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!sessionToken) {
    return null;
  }

  // Error state
  if (error) {
    return (
      <RegisterSplitLayout>
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <AlertCircle className="w-7 h-7 text-red-500" />
          </motion.div>
          <h1 className="text-xl font-bold text-[#37352F] mb-3">
            Non siamo riusciti a completare la registrazione
          </h1>
          {/* Il messaggio tecnico (es. "Edge Function returned a non-2xx status code")
              resta nella console e in Sentry: a chi si sta iscrivendo non dice nulla e
              fa sembrare rotta la piattaforma. Qui diamo una via d'uscita, sempre. */}
          <p className="text-[#787774] text-sm mb-6">
            I dati che hai inserito sono al sicuro. Puoi riprovare subito: se non funziona
            scrivici e completiamo noi l'attivazione, di solito bastano pochi minuti.
          </p>
          <div className="space-y-3">
            <button
              onClick={handleCompleteRegistration}
              className="w-full notion-register-btn"
            >
              Riprova
            </button>
            <div className="flex gap-3">
              <a
                href={`https://wa.me/393345805178?text=${encodeURIComponent(
                  'Ciao, non riesco a completare la registrazione a Flusia.'
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 notion-register-btn-ghost inline-flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
              <a
                href={`mailto:assistenza@flusia.it?subject=${encodeURIComponent(
                  'Registrazione non completata'
                )}&body=${encodeURIComponent(
                  'Ciao, non riesco a completare la registrazione a Flusia.'
                )}`}
                className="flex-1 notion-register-btn-ghost inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Email
              </a>
            </div>
            <button
              onClick={() => navigate(`/register/company?token=${sessionToken}`)}
              className="w-full text-sm text-[#787774] hover:text-[#37352F] inline-flex items-center justify-center gap-1.5 py-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Torna ai dati aziendali
            </button>
          </div>
        </div>
      </RegisterSplitLayout>
    );
  }

  // Success state
  if (completed && sessionData) {
    // Nuovo flusso: sessione già autenticata → nessun bottone, si prosegue da
    // soli verso l'onboarding (redirect già schedulato in handleCompleteRegistration).
    if (isAuthenticated) {
      return (
        <RegisterSplitLayout>
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-2xl font-bold text-[#37352F] mb-2"
            >
              Benvenuto in Flusia!
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="text-[#787774] mb-6"
            >
              Il tuo account è pronto — un attimo...
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="flex items-center justify-center"
            >
              <Loader2 className="w-6 h-6 animate-spin text-[#787774]" />
            </motion.div>
          </div>
        </RegisterSplitLayout>
      );
    }

    // Percorso storico (resume anonimo): l'utente non ha una sessione attiva,
    // resta il bottone esplicito verso /login.
    return (
      <RegisterSplitLayout>
        <div className="text-center">
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-2xl font-bold text-[#37352F] mb-2"
          >
            Benvenuto in Flusia!
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-[#787774] mb-6"
          >
            Il tuo account è stato creato con successo
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="space-y-4 mb-8"
          >
            <div className="flex items-center justify-center gap-2 text-[#0F7B6C]">
              <div className="w-5 h-5 rounded-full bg-[rgba(15,123,108,0.1)] flex items-center justify-center">
                <Shield className="w-3 h-3" />
              </div>
              <span className="text-sm font-medium">La tua prova gratuita è attiva</span>
            </div>
            <p className="text-xs text-[#787774]">
              Riceverai un'email di benvenuto con tutti i dettagli
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <p className="text-sm text-[#787774] mb-5">
              Sei pronto per iniziare! Accedi al tuo CRM e esplora tutte le funzionalità.
            </p>

            <button
              onClick={() => navigate('/login')}
              className="notion-register-btn success w-full text-base py-4"
            >
              <Rocket className="w-5 h-5" />
              <span>Accedi a Flusia</span>
            </button>
          </motion.div>
        </div>
      </RegisterSplitLayout>
    );
  }

  // Processing state
  return (
    <RegisterSplitLayout>
      <div className="text-center">
        <h1 className="text-xl font-bold text-[#37352F] mb-6">
          Finalizzazione registrazione
        </h1>

        {/* Progress Bar */}
        <div className="w-full bg-[rgba(55,53,47,0.08)] rounded-full h-2 mb-3 overflow-hidden">
          <motion.div
            className="bg-gradient-to-r from-[#2383E2] to-[#1a6dc0] h-full rounded-full"
            initial={{ width: 0 }}
            animate={{
              width: `${progress}%`,
              opacity: isWaitingForApi ? [1, 0.6, 1] : 1
            }}
            transition={isWaitingForApi ? {
              width: { duration: 0.5, ease: 'easeOut' },
              opacity: { duration: 1.2, repeat: Infinity, ease: 'easeInOut' }
            } : { duration: 0.5, ease: 'easeOut' }}
          />
        </div>

        <p className="text-xs text-[#787774] mb-6">
          {progress}% completato
        </p>

        {/* Current Message */}
        <AnimatePresence mode="wait">
          <motion.p
            key={currentMessage}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="text-sm text-[#37352F] font-medium"
          >
            {currentMessage}
          </motion.p>
        </AnimatePresence>
      </div>
    </RegisterSplitLayout>
  );
}
